/* Interrogazioni pure sullo stato del banco. Nessuna di queste funzione
   modifica niente: servono sia ai comandi sia alle schermate. */
import { groupKey, lineKey, DECORATION_ORDER, } from '../common/types.js';
/**
 * L'ordine delle taglie sul banco. Quelle fuori scala vanno in coda, in ordine.
 *
 * Sta qui e non nella vista perché adesso la chiedono in due — la vista e
 * l'etichetta della cassetta — e la vista importa i comandi, quindi metterla di
 * là avrebbe voluto dire un ciclo fra i due file.
 */
const SCALA = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'U'];
export function sortSizes(taglie) {
    return [...taglie].sort((a, b) => {
        const ia = SCALA.indexOf(a);
        const ib = SCALA.indexOf(b);
        if (ia === -1 && ib === -1)
            return a.localeCompare(b);
        if (ia === -1)
            return 1;
        if (ib === -1)
            return -1;
        return ia - ib;
    });
}
function somma(taglie) {
    if (!taglie)
        return 0;
    let n = 0;
    for (const t in taglie)
        n += taglie[t] ?? 0;
    return n;
}
/** Capi dentro una cassetta adesso. */
export function crateGarments(s, cod) {
    return somma(s.legami[cod]?.taglie);
}
/** Posti liberi in una cassetta. Piena, se ne apre un'altra (regola 5). */
export function crateSlots(s, cod) {
    if (!s.legami[cod])
        return s.capienza;
    return s.capienza - crateGarments(s, cod);
}
/** Le cassette su cui una riga si è spezzata, in ordine di apertura. */
export function cratesOfLine(s, ordine, riga) {
    return Object.keys(s.legami).filter((c) => s.legami[c]?.ordine === ordine && s.legami[c]?.riga === riga);
}
/** Capi ricevuti per una taglia: quelli al banco più quelli già partiti. */
export function receivedBySize(s, ordine, riga, t) {
    let n = s.congelati[lineKey(ordine, riga)]?.[t] ?? 0;
    for (const cod of cratesOfLine(s, ordine, riga)) {
        n += s.legami[cod]?.taglie[t] ?? 0;
    }
    return n;
}
/** Capi dichiarati fallati per una taglia. Non entrano fra i ricevuti (regola 8). */
export function defectsBySize(s, ordine, riga, t) {
    return s.fallati[lineKey(ordine, riga)]?.[t] ?? 0;
}
/**
 * Quanti capi restano da pescare dalla scatola per una taglia.
 *
 * I fallati scendono dai mancanti come i ricevuti: il capo difettoso è uscito
 * dalla scatola lo stesso, e non ne arriverà un altro al suo posto. Mai sotto
 * zero, perché dal fornitore non arriva merce in più dell'ordinato (regola 7).
 */
export function missingBySize(s, r, t) {
    const attesa = r.attese[t] ?? 0;
    const usciti = receivedBySize(s, r.ordine, r.id, t) + defectsBySize(s, r.ordine, r.id, t);
    return Math.max(0, attesa - usciti);
}
/**
 * La taglia è chiusa quando non ne manca più nessuno.
 *
 * Si appoggia a `mancantiTaglia` invece di ripetere la somma: chi accetta il
 * capo e chi disegna il tastierino devono rispondere allo stesso conto, o il
 * banco finisce per offrire una taglia che il dominio rifiuta.
 */
export function sizeClosed(s, r, t) {
    return missingBySize(s, r, t) === 0;
}
/** La riga è chiusa al banco quando non resta più niente da pescare dalla scatola. */
export function lineClosed(s, r) {
    return Object.keys(r.attese).every((t) => sizeClosed(s, r, t));
}
/** Articolo neutro: nessuna personalizzazione, salta la produzione (regola 22). */
export function neutralLine(r) {
    return r.personalizzazioni.length === 0;
}
/**
 * Il reparto di una cassetta non è un suo attributo fisso: è quello della
 * **prima personalizzazione non ancora chiusa**, nell'ordine imposto dalla
 * regola aziendale — prima la stampa, poi il ricamo (regola 19).
 */
export function currentDepartment(r, chiuse = []) {
    for (const p of DECORATION_ORDER) {
        if (r.personalizzazioni.includes(p) && !chiuse.includes(p))
            return p;
    }
    return null;
}
/**
 * L'etichetta di una taglia è valida finché dichiara la quantità che c'è
 * davvero. Contare ancora la invalida (regola 3-bis): dichiarava un numero
 * che non è più vero.
 */
export function labelValid(s, cod, t) {
    const g = s.etichette[groupKey(cod, t)];
    if (!g)
        return false;
    return g.quantita === (s.legami[cod]?.taglie[t] ?? 0);
}
/** Le taglie di una cassetta che aspettano un'etichetta: mai stampata, o invalidata. */
export function toPrint(s, cod) {
    const l = s.legami[cod];
    if (!l)
        return [];
    return Object.keys(l.taglie)
        .filter((t) => (l.taglie[t] ?? 0) > 0 && !labelValid(s, cod, t))
        .sort();
}
/** Non si conferma una cassetta con taglie senza etichetta (regola 3-ter). */
export function confirmable(s, cod) {
    return !!s.legami[cod] && crateGarments(s, cod) > 0 && toPrint(s, cod).length === 0;
}
/** Cassette aperte al banco. */
export function cratesOnBench(s) {
    return Object.keys(s.legami);
}
/** Come si presenta una cassetta rispetto al capo agganciato adesso. */
export function crateState(s, cod) {
    const l = s.legami[cod];
    if (!l)
        return { s: 'libera' };
    if (s.agganciato && l.ordine === s.agganciato.ordine && l.riga === s.agganciato.riga) {
        return { s: 'questa', piena: crateSlots(s, cod) <= 0 };
    }
    return { s: 'altra', ordine: l.ordine, riga: l.riga };
}
/** Il contenuto dichiarato di una cassetta: quello che l'etichetta elettronica scrive. */
export function crateContents(s, cod, cat) {
    const l = s.legami[cod];
    if (!l)
        return null;
    const r = cat.riga(l.ordine, l.riga);
    if (!r)
        return null;
    return {
        ordine: r.ordine,
        cliente: r.cliente,
        sku: r.sku,
        articolo: r.articolo,
        colore: r.colore,
        taglie: { ...l.taglie },
        capi: crateGarments(s, cod),
        neutra: neutralLine(r),
        ...(r.anteprima === undefined ? {} : { anteprima: r.anteprima }),
        ...(r.genere === undefined ? {} : { genere: r.genere }),
    };
}
//# sourceMappingURL=crate.js.map